/*
   Real-backend adapters for the /admin/users page.

   Two paginated + searchable + sortable queries:
     - usersQuery(params) — GET /api/v1/users with page/page_size/search/sort/role
     - keysQuery()        — GET /api/v1/api-keys (small; not yet paginated server-side)

   Backend role enum is `admin | reviewer | sme | viewer | client`. The FE calls the
   SME role "specialist" as its display label, so we translate `sme ↔ specialist` at
   the boundary — the FE keeps its friendlier vocabulary, the wire keeps its spec
   vocabulary.

   Fields the wire does not carry today are stubbed with sensible defaults:
     - user.lastSeen    → null (no login-history endpoint yet)
     - user.isBootstrap → true only for `admin@pyxisrc.com` (best-effort guess)
     - key.createdBy    → resolved from /users by created_by_id when possible
     - key.fromEnv      → true when created_by_id is null (env-provisioned)
*/

import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query"

import { http } from "@/lib/http"
import { fromPageWire, toQueryParams, type Page, type PageWire } from "@/lib/pagination"
import { qk } from "@/lib/query-keys"
import { Role, toRole } from "@/lib/roles"
import type { TableQueryParams } from "@/lib/table"

import {
  ConsoleRole,
  type ConsoleUser,
  type ConsoleUserId,
  type ServiceKey,
  type ServiceKeyId,
} from "./users-keys-data"

// ── Wire types ────────────────────────────────────────────────────────

interface UserOutWire {
  id: number
  email: string
  full_name: string | null
  role: string
  is_active: boolean
  created_at: string
  invited_at: string | null
  accepted_at: string | null
}

interface ApiKeyOutWire {
  id: number
  name: string
  prefix: string
  created_by_id: number | null
  created_at: string
  revoked_at: string | null
  last_used_at?: string | null
}

// ── Role mapping ─────────────────────────────────────────────────────
// The console displays SME as "Specialist" — friendlier vocabulary. Translate at the
// wire boundary so the app talks in ConsoleRole and the backend talks in Role.

function fromWireRole(role: string): ConsoleRole {
  const known = toRole(role)
  if (known === Role.SME) return ConsoleRole.SPECIALIST
  if (known === Role.ADMIN) return ConsoleRole.ADMIN
  if (known === Role.REVIEWER) return ConsoleRole.REVIEWER
  if (known === Role.VIEWER) return ConsoleRole.VIEWER
  if (known === Role.CLIENT) return ConsoleRole.CLIENT
  return ConsoleRole.VIEWER
}

export function toWireRole(role: ConsoleRole): Role {
  if (role === ConsoleRole.SPECIALIST) return Role.SME
  return role as Role
}

// ── Adapters ─────────────────────────────────────────────────────────

function toConsoleUser(u: UserOutWire): ConsoleUser {
  // Backend has no login-history yet; accepting an invite is the earliest sign-in we can prove,
  // so use it as the lastSeen fallback rather than showing "Never signed in" for someone who
  // clearly has. Once a real login-history endpoint lands, replace this with the true value.
  return {
    id: String(u.id) as ConsoleUserId,
    email: u.email,
    name: u.full_name,
    role: fromWireRole(u.role),
    isBootstrap: u.email === "admin@pyxisrc.com",
    createdAt: u.created_at,
    lastSeen: u.accepted_at,
    invitedAt: u.invited_at,
    acceptedAt: u.accepted_at,
    isActive: u.is_active,
  }
}

function toServiceKey(k: ApiKeyOutWire, usersById: Map<number, UserOutWire>): ServiceKey {
  const creator = k.created_by_id != null ? usersById.get(k.created_by_id) : null
  return {
    id: String(k.id) as ServiceKeyId,
    name: k.name,
    prefix: k.prefix,
    createdAt: k.created_at,
    createdBy: creator
      ? (creator.full_name ?? creator.email)
      : k.created_by_id != null
        ? `User #${k.created_by_id}`
        : "environment",
    lastUsedAt: k.last_used_at ?? null,
    fromEnv: k.created_by_id == null,
  }
}

// ── Users: paginated page + a small unpaginated map for key attribution ──

export interface UsersPageParams {
  page: number
  pageSize: number
  search: string
  sort: { field: string; dir: "asc" | "desc" } | null
  role?: string | undefined
}

async function fetchUsersPage(
  params: UsersPageParams,
  signal: AbortSignal,
): Promise<Page<ConsoleUser>> {
  const extra: Record<string, string | undefined> = {}
  if (params.role) extra["role"] = params.role
  const wire = await http.get<PageWire<UserOutWire>>("/api/v1/users", {
    params: toQueryParams(
      {
        page: params.page,
        pageSize: params.pageSize,
        sort: params.sort,
        filters: {},
        search: params.search,
      } satisfies TableQueryParams,
      extra,
    ),
    signal,
  })
  return fromPageWire(wire, toConsoleUser)
}

export const usersQuery = (params: UsersPageParams) =>
  queryOptions({
    queryKey: qk.users.list(params),
    queryFn: ({ signal }) => fetchUsersPage(params, signal),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })

async function fetchUsersByIdMap(signal: AbortSignal): Promise<Map<number, UserOutWire>> {
  const wire = await http.get<PageWire<UserOutWire>>("/api/v1/users", {
    params: { page_size: 100 },
    signal,
  })
  return new Map(wire.items.map((u) => [u.id, u]))
}

export const usersMapQuery = () =>
  queryOptions({
    queryKey: [...qk.users.all, "list", { form: "map" }] as const,
    queryFn: ({ signal }) => fetchUsersByIdMap(signal),
    staleTime: 60_000,
  })

// ── API keys: /api-keys is not yet paginated server-side. ────────────────

async function fetchApiKeys(qc: QueryClient, signal: AbortSignal): Promise<ServiceKey[]> {
  // Warm the users map from cache; single source of truth for name attribution.
  const usersById = await qc.ensureQueryData(usersMapQuery())
  const raw = await http.get<ApiKeyOutWire[]>("/api/v1/api-keys", { signal })
  return raw
    .filter((k) => k.revoked_at == null)
    .map((k) => toServiceKey(k, usersById))
}

export const apiKeysQuery = (qc: QueryClient) =>
  queryOptions({
    queryKey: qk.apiKeys.list(),
    queryFn: ({ signal }) => fetchApiKeys(qc, signal),
    staleTime: 30_000,
  })

export function useApiKeysQuery(qc: QueryClient): UseQueryResult<ServiceKey[]> {
  return useQuery(apiKeysQuery(qc))
}

// ── Mutations ────────────────────────────────────────────────────────

export interface CreateUserInput {
  email: string
  fullName: string | null
  role: ConsoleRole
  // Password path: caller sets one and shares it out of band. Invite path: server emails a link.
  mode: { kind: "password"; password: string } | { kind: "invite" }
}

interface CreateUserWire {
  email: string
  full_name: string | null
  role: string
  password?: string
  send_invite?: boolean
}

async function createUserRequest(input: CreateUserInput): Promise<ConsoleUser> {
  const body: CreateUserWire = {
    email: input.email.trim(),
    full_name: input.fullName?.trim() || null,
    role: toWireRole(input.role),
  }
  if (input.mode.kind === "password") body.password = input.mode.password
  else body.send_invite = true
  const wire = await http.post<UserOutWire, CreateUserWire>("/api/v1/users", body)
  return toConsoleUser(wire)
}

export function useCreateUserMutation(): UseMutationResult<
  ConsoleUser,
  Error,
  CreateUserInput
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createUserRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users.all })
    },
  })
}

async function resendInviteRequest(userId: ConsoleUserId): Promise<void> {
  await http.post(`/api/v1/users/${userId}/resend-invite`)
}

export function useResendInviteMutation(): UseMutationResult<void, Error, ConsoleUserId> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: resendInviteRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users.all })
    },
  })
}

async function deleteUserRequest(userId: ConsoleUserId): Promise<void> {
  await http.delete(`/api/v1/users/${userId}`)
}

export function useDeleteUserMutation(): UseMutationResult<void, Error, ConsoleUserId> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteUserRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users.all })
    },
  })
}
